import { useState, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Inbox, Send, Plus, LogOut, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { getConnectedAccounts, ConnectedAccount } from '../../services/userService'

interface SidebarProps {
  onCompose: () => void
  isOpen?: boolean
  onClose?: () => void
}

export default function Sidebar({ onCompose, isOpen = false, onClose }: SidebarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([])
  const [loadingAccounts, setLoadingAccounts] = useState(true)

  useEffect(() => {
    const loadAccounts = async () => {
      if (!user) {
        setAccounts([])
        setLoadingAccounts(false)
        return
      }

      try {
        const fetchedAccounts = await getConnectedAccounts(user.uid)
        setAccounts(fetchedAccounts)
      } catch (error) {
        console.error('Error loading accounts:', error)
      } finally {
        setLoadingAccounts(false)
      }
    }

    loadAccounts()
  }, [user])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (onClose && window.innerWidth < 1024) {
      onClose()
    }
  }

  const handleComposeClick = () => {
    onCompose()
    // Close sidebar on mobile after compose
    if (onClose && window.innerWidth < 1024) {
      onClose()
    }
  }

  // Sidebar content - shared between mobile and desktop
  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-200">
              <Inbox className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                InboxOne
              </h1>
            </div>
          </div>
          {/* Close button - only visible on mobile */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 -mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Compose Button */}
      <div className="p-4">
        <button
          onClick={handleComposeClick}
          className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
        >
          <Plus size={20} />
          Compose
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 overflow-y-auto">
        <NavLink
          to="/inbox"
          onClick={handleNavClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
              isActive
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-gray-700 hover:bg-gray-100'
            }`
          }
        >
          <Inbox size={20} />
          Inbox
        </NavLink>

        <NavLink
          to="/sent"
          onClick={handleNavClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
              isActive
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-gray-700 hover:bg-gray-100'
            }`
          }
        >
          <Send size={20} />
          Sent
        </NavLink>
      </nav>

      {/* Connected Accounts */}
      <div className="p-4 border-t border-gray-200">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Accounts
        </h3>
        <div className="space-y-2">
          {loadingAccounts ? (
            <div className="flex items-center justify-center py-2">
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : accounts.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No accounts connected</p>
          ) : (
            accounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center gap-2 text-sm text-gray-600"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: account.color }}
                />
                <span className="truncate">{account.email}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* User & Logout */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt="Profile"
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium text-sm">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.displayName || 'User'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 py-2 px-4 rounded-lg transition-colors text-sm"
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    </>
  )

  return (
    <>
      {/* Desktop Sidebar - Fixed, always visible on lg+ screens */}
      <aside className="hidden lg:flex lg:w-64 bg-white border-r border-gray-200 flex-col h-full">
        {sidebarContent}
      </aside>

      {/* Mobile/Tablet Drawer - Slide in from left */}
      <div className="lg:hidden">
        {/* Backdrop overlay */}
        <div
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ease-in-out ${
            isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Drawer */}
        <aside
          className={`fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-white z-50 flex flex-col shadow-2xl transform transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {sidebarContent}
        </aside>
      </div>
    </>
  )
}
